/**
 * The rationale decision. Pure, so it is tested in Node.
 *
 * On Android 13+ a DENIED `POST_NOTIFICATIONS` prompt cannot be shown again
 * from the app, so the one prompt the app gets is spent only after the
 * person read what will be sent and pressed "Ativar". The rationale is shown
 * once per signed-in person: the answer is remembered in Preferences under
 * {@link CONSENT_KEY}, and forgotten when the session ends, so the next
 * person to sign in on this device is asked for themselves (`D94`:
 * registering is the opt-in, and it is per account).
 */
import type { PushRegistrationStatus } from "../../native";

/** Preferences key of the remembered answer. Not a secret. */
export const CONSENT_KEY = "push.rationale";

export type PushConsent = "accepted" | "declined";

/** Reads a stored value back, ignoring anything this bundle did not write. */
export function parseConsent(value: string | null): PushConsent | null {
  return value === "accepted" || value === "declined" ? value : null;
}

/**
 * Ask only when this person never opted in on this device (`off`: the boot
 * path found no registered token) and has not answered the rationale.
 *
 * - `registered` / `working`: already on.
 * - `denied`: opted in before, then revoked in system settings; the prompt
 *   is gone, and the profile's switch is where they turn it back on.
 * - `failed`: a network or native error, not a question for the person.
 * - `unsupported` / `idle`: the web, or nothing read yet.
 */
export function shouldAskRationale(
  status: PushRegistrationStatus,
  consent: PushConsent | null
): boolean {
  return status === "off" && consent === null;
}

/**
 * Whether resuming the app re-runs `refreshPush`: only for a person who opted
 * in (`registered`), whose permission was revoked and may be back
 * (`denied`), or whose last attempt failed (`failed`). Never while one is in
 * flight, and never for `off` — `refreshPush` would find no token anyway.
 */
export function shouldRefreshOnResume(status: PushRegistrationStatus): boolean {
  return status === "registered" || status === "denied" || status === "failed";
}
